import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { PORTAL_URL } from '../../lib/siteMode';
import LoadingScreen from '../shared/LoadingScreen';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const loading = useAuthStore((s) => s.loading);
  const initialized = useAuthStore((s) => s.initialized);
  const location = useLocation();

  // Show loading spinner until auth state is fully resolved.
  if (!initialized || loading) {
    return <LoadingScreen />;
  }

  // Not authenticated → sign in.
  // Anonymous users (under-13 consent flow) are allowed through and handled
  // by the profile-based checks below.
  if (!user) {
    if (location.pathname === '/age-gate') return <>{children}</>;
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Authenticated but no profile → age gate (new user completing sign-up)
  if (!profile) {
    if (location.pathname === '/age-gate') return <>{children}</>;
    return <Navigate to="/age-gate" replace />;
  }

  // Profile exists but onboarding not complete → onboarding or consent gate
  if (!profile.hasCompletedSignUp) {
    if (profile.subscriptionStatus === 'pending_consent') {
      if (location.pathname === '/age-gate') return <>{children}</>;
      return <Navigate to="/age-gate" state={{ consentPending: true }} replace />;
    }
    if (profile.subscriptionStatus === 'rejected') {
      if (location.pathname === '/age-gate') return <>{children}</>;
      return <Navigate to="/age-gate" state={{ initialStep: 'rejected' }} replace />;
    }
    if (location.pathname === '/onboarding') return <>{children}</>;
    return <Navigate to="/onboarding" replace />;
  }

  // Revoked subscription — parent has cut off access
  if (profile.subscriptionStatus === 'revoked') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow">
          <h1 className="text-2xl font-bold text-gray-900">
            Account Access Revoked
          </h1>
          <p className="mt-3 text-sm text-gray-600">
            Your child&apos;s account access has been revoked by a parent.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Visit{' '}
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand-600 underline hover:text-brand-700"
            >
              parent.omore.app
            </a>{' '}
            to manage their account.
          </p>
        </div>
      </div>
    );
  }

  // Expired subscription → paywall (except on /parent)
  if (
    profile.subscriptionStatus === 'expired' &&
    location.pathname !== '/parent'
  ) {
    return <Navigate to="/auth" state={{ expired: true }} replace />;
  }

  return <>{children}</>;
}
